import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  DataTable,
  EmptyState,
  Heading,
  SearchInput,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
  tabularNumsClass,
  type DataTableColumn,
} from "@lendcord/concord";
import { CreditorStatCard } from "@/features/creditor/components/CreditorStatCard";
import { formatIsoDate } from "@/app/dateFormat";
import { useGetEmiRecordsQuery, useGetEmiSummaryQuery } from "@/features/creditor/api/creditor.api";
import type { EmiRecord, EmiStatus } from "@/features/creditor/creditor.types";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const countFormatter = new Intl.NumberFormat("en-IN");

const PAGE_SIZE = 10;

const STATUS_TABS: Array<{ value: "all" | EmiStatus; labelId: string }> = [
  { value: "all", labelId: "lender.creditor.statusTabAll" },
  { value: "upcoming", labelId: "lender.creditor.statusTabUpcoming" },
  { value: "overdue", labelId: "lender.creditor.statusTabOverdue" },
  { value: "paid", labelId: "lender.creditor.statusTabPaid" },
];

const STATUS_LABEL_IDS: Record<EmiStatus, string> = {
  paid: "lender.creditor.statusTabPaid",
  upcoming: "lender.creditor.statusTabUpcoming",
  overdue: "lender.creditor.statusTabOverdue",
};

function handleDownloadData() {
  // TODO: wire to a real export once it exists.
}

interface EmiDashboardProps {
  onBack: () => void;
}

export function EmiDashboard({ onBack }: EmiDashboardProps) {
  const intl = useIntl();
  const { data: summary } = useGetEmiSummaryQuery();
  const { data: recordsData, isLoading: isRecordsLoading } = useGetEmiRecordsQuery();
  const records = recordsData?.items;
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | EmiStatus>("all");
  const [page, setPage] = useState(1);

  const filteredRecords =
    statusFilter === "all" ? (records ?? []) : (records ?? []).filter((record) => record.status === statusFilter);
  const totalCount = filteredRecords.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = filteredRecords.slice(startIndex, startIndex + PAGE_SIZE);
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  function handleStatusFilterChange(value: string) {
    setStatusFilter(value as "all" | EmiStatus);
    setPage(1);
  }

  const columns: DataTableColumn<EmiRecord>[] = [
    {
      id: "loanId",
      header: intl.formatMessage({ id: "lender.creditor.columnLoanId" }),
      cell: (record) => record.loanId,
      cellClassName: tabularNumsClass,
    },
    {
      id: "borrower",
      header: intl.formatMessage({ id: "lender.creditor.columnBorrower" }),
      cell: (record) => record.borrower,
    },
    {
      id: "emiAmount",
      header: intl.formatMessage({ id: "lender.creditor.columnEmiAmount" }),
      cell: (record) => currencyFormatter.format(record.emiAmount),
      cellClassName: tabularNumsClass,
    },
    {
      id: "dueDate",
      header: intl.formatMessage({ id: "lender.creditor.columnDueDate" }),
      cell: (record) => formatIsoDate(record.dueDate),
      cellClassName: tabularNumsClass,
    },
    {
      id: "paidDate",
      header: intl.formatMessage({ id: "lender.creditor.columnPaidDate" }),
      cell: (record) => (record.paidDate ? formatIsoDate(record.paidDate) : "–"),
      cellClassName: tabularNumsClass,
    },
    {
      id: "status",
      header: intl.formatMessage({ id: "lender.creditor.columnStatus" }),
      cell: (record) => <FormattedMessage id={STATUS_LABEL_IDS[record.status]} />,
    },
    {
      id: "principal",
      header: intl.formatMessage({ id: "lender.creditor.columnPrincipal" }),
      cell: (record) => currencyFormatter.format(record.principal),
      cellClassName: tabularNumsClass,
    },
    {
      id: "interest",
      header: intl.formatMessage({ id: "lender.creditor.columnInterest" }),
      cell: (record) => currencyFormatter.format(record.interest),
      cellClassName: tabularNumsClass,
    },
    {
      id: "outstanding",
      header: intl.formatMessage({ id: "lender.creditor.columnOutstanding" }),
      cell: (record) => currencyFormatter.format(record.outstanding),
      cellClassName: tabularNumsClass,
    },
    {
      id: "tenure",
      header: intl.formatMessage({ id: "lender.creditor.columnTenure" }),
      cell: (record) => `${record.tenureMonths}m`,
      cellClassName: tabularNumsClass,
    },
    {
      id: "mode",
      header: intl.formatMessage({ id: "lender.creditor.columnMode" }),
      cell: (record) => record.mode,
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.creditor.columnAction" }),
      cell: () => <FormattedMessage id="lender.creditor.actionViewLink" />,
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            aria-label={intl.formatMessage({ id: "lender.creditor.backToOverviewLabel" })}
            className="text-xl text-text-muted hover:text-text"
          >
            ‹
          </button>
          <Heading level={2}>
            <FormattedMessage id="lender.creditor.emiDashboardHeading" />
          </Heading>
        </div>
        <Button type="button" variant="secondary" className="border-border text-text hover:bg-surface">
          <FormattedMessage id="lender.creditor.periodThisMonth" /> ⌄
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-5">
        <CreditorStatCard
          labelId="lender.creditor.totalEmiCollectedLabel"
          value={summary ? currencyFormatter.format(summary.totalEmiCollected) : ""}
          loading={!summary}
        />
        <CreditorStatCard
          labelId="lender.creditor.emisDueTodayLabel"
          value={summary ? countFormatter.format(summary.emisDueToday) : ""}
          loading={!summary}
        />
        <CreditorStatCard
          labelId="lender.creditor.overdueAmountLabel"
          value={summary ? currencyFormatter.format(summary.overdueAmount) : ""}
          loading={!summary}
        />
        <CreditorStatCard
          labelId="lender.creditor.activeLoansLabel"
          value={summary ? countFormatter.format(summary.activeLoans) : ""}
          loading={!summary}
        />
        <CreditorStatCard
          labelId="lender.creditor.recoveryRateLabel"
          value={summary ? `${summary.recoveryRatePercent}%` : ""}
          loading={!summary}
        />
      </div>

      <Tabs value={statusFilter} onValueChange={handleStatusFilterChange} className="mb-4">
        <TabsList className="gap-6 bg-transparent p-0">
          {STATUS_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="border-0 px-0 text-text-muted data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-accent-600 data-[state=active]:shadow-none"
            >
              <FormattedMessage id={tab.labelId} />
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-sm flex-1">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery("")}
            placeholder={intl.formatMessage({ id: "lender.creditor.searchLoanPlaceholder" })}
          />
        </div>
        <Button type="button" onClick={handleDownloadData}>
          <FormattedMessage id="lender.creditor.downloadDataButton" />
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={pageItems}
        rowKey={(record) => record.loanId}
        loading={isRecordsLoading}
        titleColumnId="borrower"
        metaColumnId="status"
        emptyState={
          <EmptyState>
            <FormattedMessage id="lender.creditor.noMatchingRecordsMessage" />
          </EmptyState>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Text size="sm" variant="muted">
          <FormattedMessage id="lender.creditor.rowsPerPageLabel" values={{ count: PAGE_SIZE }} />
        </Text>
        <div className="flex items-center gap-3">
          <Text size="sm" variant="muted">
            <FormattedMessage
              id="lender.creditor.paginationRangeLabel"
              values={{ start: rangeStart, end: rangeEnd, total: totalCount }}
            />
          </Text>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ‹
          </button>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
